import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { ComboBox, DetailsList, Spinner, SpinnerSize, Dropdown, IconButton } from '@fluentui/react'
import { debounce } from 'lodash'
import { getPlayerDetail, getPlayerOptions } from 'actions/players'
import { selectPlayerDetail, selectSearchOptions1 } from 'selectors/players'

import StatCard from './shared/StatCard'
import './Player.scss'

// ─── Elo line chart (custom SVG, no extra dependency) ────────────────────────

const EloLineChart = ({ data }) => {
  if (!data || data.length < 2) {
    return <div className="chartEmpty">Not enough data to display.</div>
  }

  const W = 800, H = 220
  const pad = { top: 20, right: 20, bottom: 50, left: 65 }
  const iW = W - pad.left - pad.right
  const iH = H - pad.top - pad.bottom

  const elos = data.map(d => d.elo)
  const minE = Math.min(...elos)
  const maxE = Math.max(...elos)
  const span = maxE - minE || 200
  const minY = minE - span * 0.12
  const maxY = maxE + span * 0.12

  const xOf = i => data.length > 1 ? (i / (data.length - 1)) * iW : iW / 2
  const yOf = e => iH - ((e - minY) / (maxY - minY)) * iH

  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(d.elo).toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L${iW},${iH} L0,${iH} Z`

  // Y axis ticks
  const step = span <= 150 ? 50 : span <= 400 ? 100 : 200
  const yTicks = []
  for (let v = Math.ceil(minY / step) * step; v <= maxY; v += step) yTicks.push(v)

  // X axis ticks — every 6 or 12 months depending on density
  const xInterval = data.length <= 30 ? 3 : data.length <= 72 ? 6 : 12
  const xTicks = data.reduce((acc, d, i) => {
    if (i % xInterval === 0 || i === data.length - 1) acc.push({ ...d, i })
    return acc
  }, [])

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="eloSvg">
      <defs>
        <linearGradient id="eloGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1c1c1c" stopOpacity={0.25} />
          <stop offset="100%" stopColor="#1c1c1c" stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <g transform={`translate(${pad.left},${pad.top})`}>
        {yTicks.map(v => (
          <line key={v} x1={0} y1={yOf(v)} x2={iW} y2={yOf(v)}
                stroke="#ebebeb" strokeWidth={1} />
        ))}
        <path d={areaPath} fill="url(#eloGrad)" />
        <path d={linePath} fill="none" stroke="#1c1c1c" strokeWidth={2.5}
              strokeLinejoin="round" strokeLinecap="round" />
        {yTicks.map(v => (
          <text key={v} x={-10} y={yOf(v)} textAnchor="end"
                dominantBaseline="middle" fontSize={11} fill="#999">
            {v}
          </text>
        ))}
        {xTicks.map(({ month, i }) => (
          <g key={month} transform={`translate(${xOf(i)},${iH + 6})`}>
            <line x1={0} y1={0} x2={0} y2={4} stroke="#ccc" />
            <text y={14} textAnchor="end" fontSize={10} fill="#999"
                  transform="rotate(-35)">
              {month}
            </text>
          </g>
        ))}
        <line x1={0} y1={0} x2={0} y2={iH} stroke="#ddd" />
        <line x1={0} y1={iH} x2={iW} y2={iH} stroke="#ddd" />
      </g>
    </svg>
  )
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MATCH_TYPE_OPTIONS = [
  { key: '', text: 'All' },
  { key: 'ELO', text: 'ELO' },
  { key: 'TAP', text: 'TAP' },
  { key: 'DAS', text: 'DAS' },
  { key: 'FRIENDLY', text: 'FRIENDLY' },
]

const matchColumns = [
  { key: 'date', name: 'Date', minWidth: 100, maxWidth: 110,
    onRender: item => item.date ? new Date(item.date).toLocaleDateString() : '—' },
  { key: 'event', name: 'Event', minWidth: 120, fieldName: 'event' },
  { key: 'round', name: 'Round', minWidth: 80, maxWidth: 100, fieldName: 'round_game' },
  { key: 'matchType', name: 'Type', minWidth: 60, maxWidth: 80, fieldName: 'match_type' },
  { key: 'opponent', name: 'Opponent', minWidth: 130, fieldName: 'opponent' },
  { key: 'score', name: 'Score', minWidth: 70, maxWidth: 80,
    onRender: item => `${item.player_score ?? '?'} – ${item.opponent_score ?? '?'}` },
  { key: 'result', name: 'Result', minWidth: 60, maxWidth: 70,
    onRender: item => (
      <span className={`resultBadge ${item.result}`}>
        {item.result ? item.result[0].toUpperCase() + item.result.slice(1) : '—'}
      </span>
    )},
]

// ─── Main component ───────────────────────────────────────────────────────────

const Player = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { id } = useParams()

  const detail = useSelector(selectPlayerDetail)
  const searchOptions = useSelector(selectSearchOptions1)

  // Only show the detail spinner when a specific player is being fetched —
  // not during autocomplete searches (which also set the shared isLoading flag).
  const isDetailLoading = !!id && (!detail || detail.id !== id)

  const comboRef = useRef(null)
  const [displayOptions, setDisplayOptions] = useState([])
  const [selectedKey, setSelectedKey] = useState(id || null)
  const [matchTypeFilter, setMatchTypeFilter] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')

  // Sync search options from Redux into local state
  useEffect(() => {
    if (searchOptions.length > 0) setDisplayOptions(searchOptions)
  }, [searchOptions])

  // Load player when URL param changes
  useEffect(() => {
    if (id) {
      setSelectedKey(id)
      dispatch(getPlayerDetail(id))
    }
  }, [id, dispatch])

  const fetchOptions = useCallback(
    debounce((filter) => {
      if (filter) dispatch(getPlayerOptions(filter, true))
    }, 300),
    [dispatch]
  )

  useEffect(() => () => fetchOptions.cancel(), [fetchOptions])

  // Mirrors the Compare page pattern: debounce the API call but open the
  // dropdown immediately so the user sees results as they arrive.
  const handleInputChange = (text) => {
    fetchOptions(text)
    if (text) comboRef.current?.focus(true)
  }

  const handleSelect = (_, option) => {
    if (option) {
      setSelectedKey(option.key)
      history.push(`/player/${option.key}`)
    }
  }

  const filteredMatches = useMemo(() => {
    if (!detail?.match_history) return []
    let matches = detail.match_history
    if (matchTypeFilter) {
      matches = matches.filter(m => m.match_type === matchTypeFilter)
    }
    if (sortOrder === 'asc') {
      matches = [...matches].reverse()
    }
    return matches
  }, [detail, matchTypeFilter, sortOrder])

  const streakLabel = detail
    ? `${detail.current_streak} ${detail.current_streak_type === 'win' ? 'W' : 'L'}`
    : '—'
  const streakClass = detail?.current_streak_type === 'win' ? 'statWin' : 'statLoss'

  return (
    <div className="playerDetail">
      <div className="searchSection">
        <ComboBox
          componentRef={comboRef}
          label="Search for a player"
          placeholder="Start typing a name..."
          allowFreeform
          autoComplete="off"
          openMenuOnClick
          selectedKey={selectedKey}
          options={displayOptions}
          onChange={handleSelect}
          onInputValueChange={handleInputChange}
        />
      </div>

      {isDetailLoading && (
        <div className="loadingState">
          <Spinner size={SpinnerSize.large} label="Loading player stats..." />
        </div>
      )}

      {!isDetailLoading && detail && (
        <>
          {/* Header */}
          <div className="playerHeader">
            <h1 className="playerName">{detail.name}</h1>
            <span className="eloBadge">Elo {detail.current_elo}</span>
            {detail.current_glicko && (
              <span className="glickoBadge">Glicko {detail.current_glicko}</span>
            )}
          </div>

          {/* Stats rows */}
          <div className="statsRow">
            <StatCard title="Current Elo"     value={detail.current_elo} />
            <StatCard title="Peak Elo"        value={detail.peak_elo} />
            <StatCard title="Win Rate"        value={`${detail.win_rate}%`} />
            <StatCard title="Matches Played"  value={detail.matches_played} />
          </div>
          <div className="statsRow">
            <StatCard title="Win – Loss"      value={`${detail.wins} – ${detail.losses}`} />
            <StatCard title="Best Win Streak" value={detail.longest_win_streak} />
            <StatCard title="Current Streak"
                      value={<span className={streakClass}>{streakLabel}</span>} />
            <StatCard title="Last Match"
                      value={detail.last_match_date
                        ? new Date(detail.last_match_date).toLocaleDateString()
                        : '—'} />
          </div>

          {/* Elo history chart */}
          <div className="chartCard">
            <h3>Elo Rating History</h3>
            <EloLineChart data={detail.elo_history} />
          </div>

          {/* Match history */}
          <div className="matchesCard">
            <h3>Match History</h3>
            <div className="matchFilters">
              <Dropdown
                label="Type"
                selectedKey={matchTypeFilter}
                options={MATCH_TYPE_OPTIONS}
                onChange={(_, option) => { if (option) setMatchTypeFilter(option.key) }}
                styles={{ root: { minWidth: 120 } }}
              />
              <div className="sortToggle">
                <span className="sortLabel">Sort</span>
                <IconButton
                  iconProps={{ iconName: sortOrder === 'desc' ? 'SortDown' : 'SortUp' }}
                  title={sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
                  onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                />
              </div>
            </div>
            <div className="matchTableScroll" data-is-scrollable={true}>
              <div style={{ minWidth: 700 }}>
                <DetailsList
                  items={filteredMatches}
                  columns={matchColumns}
                  getKey={(_, i) => i}
                  setKey="none"
                  selectionMode={0}
                  checkboxVisibility={2}
                  compact
                />
              </div>
            </div>
          </div>
        </>
      )}

      {!isDetailLoading && !detail && id && (
        <div className="emptyState">Player not found.</div>
      )}

      {!id && (
        <div className="emptyState">Search for a player above to see their stats.</div>
      )}
    </div>
  )
}

export default Player
